import { ChangeDetectionStrategy, Component, computed, inject, input, signal } from '@angular/core';
import { RouterLink } from '@angular/router';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';

import { AUTH_CONFIG_IDS } from '../../../auth/auth.config';
import { ApprovedLegalDocumentsModalComponent } from '../../../legal-documents/components/approved-legal-documents-modal/approved-legal-documents-modal.component';
import { LegalDocument } from '../../../legal-documents/model/LegalDocument';
import { LEGAL_DOCUMENTS_TYPES } from '../../../legal-documents/model/LegalDocumentType';
import { getApprovedLegalDocuments, getLegalNoticeUrl } from '../../../legal-documents/store/selectors/legalDocument';
import { IconComponent } from '../../../shared/components/icons/icon/icon.component';
import { MenuComponent } from '../../../shared/components/menu/menu.component';
import { PopoverComponent } from '../../../shared/components/popover/popover.component';
import { DEFAULT_LOCALE } from '../../../shared/i18n/i18n.config';
import { User } from '../../../shared/models/User';
import { AnalyticsService } from '../../../shared/services/analytics.service';
import { LocalStorageService } from '../../../shared/services/localStorage.service';
import { SecurityService } from '../../../shared/services/SecurityService';
import { injectFromStore } from '../../../shared/store/selectors/injectFromStore';
import { getFaqPageUrl } from '../../../utils/url';
import { LanguageSelectorComponent } from '../language-selector/language-selector.component';
import {
    LOCALE_COUNTRY_STORAGE_KEY,
    resolveLanguageCode,
    resolveSelectedOption,
} from '../language-selector/locale-options';

@Component({
    selector: 'app-side-nav',
    imports: [
        TranslatePipe,
        IconComponent,
        PopoverComponent,
        MenuComponent,
        RouterLink,
        ApprovedLegalDocumentsModalComponent,
        LanguageSelectorComponent,
    ],
    templateUrl: './side-nav.component.html',
    styleUrl: './side-nav.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SideNavComponent {
    private readonly analyticsService = inject(AnalyticsService);
    private readonly securityService = inject(SecurityService);
    private readonly translateService = inject(TranslateService);
    private readonly localStorageService = inject(LocalStorageService);

    private readonly analyticsDocTypes = {
        [LEGAL_DOCUMENTS_TYPES.GENERAL_DATA_PROTECTION_REGULATION]: 'data privacy',
        [LEGAL_DOCUMENTS_TYPES.GENERAL_TERMS_AND_CONDITIONS_OF_USE]: 'CGU',
    };

    readonly userLogin = input<User | undefined>(undefined);

    readonly faqUrl = computed(() => getFaqPageUrl(this.userLogin()?.country ?? 'GB'));
    readonly legalNoticeUrl = injectFromStore(getLegalNoticeUrl);

    readonly legalDocumentApproval = injectFromStore(getApprovedLegalDocuments);
    readonly legalDocumentApprovalModalOpen = signal(false);

    readonly languageSelectorOpen = signal(false);

    readonly localeTriggerLabel = this.resolveLocaleTriggerLabel();

    onSettingsClick(popover: PopoverComponent): void {
        popover.toggle();
        if (popover.isOpen()) {
            this.analyticsService.clickGlobalMenu();
        }
    }

    openLanguageSelector(popover: PopoverComponent): void {
        popover.close();
        this.languageSelectorOpen.set(true);
    }

    private resolveLocaleTriggerLabel(): string {
        const language = resolveLanguageCode(this.translateService.getCurrentLang() || DEFAULT_LOCALE);
        const country = this.localStorageService.getItem(LOCALE_COUNTRY_STORAGE_KEY);
        return resolveSelectedOption(language, country)?.country ?? '';
    }

    onConfigAlertClick(popover: PopoverComponent): void {
        this.analyticsService.clickAlertConfig();
        popover.close();
    }

    showSignedLegalDocModal(popover: PopoverComponent): void {
        this.legalDocumentApprovalModalOpen.set(true);
        popover.close();
    }

    logLegalDocumentAccessInAnalytics(document: LegalDocument): void {
        const docType = this.analyticsDocTypes[document.documentType];
        this.analyticsService.accessGlobalMenuDocument(docType);
    }

    onLegalNoticeClick(popover: PopoverComponent): void {
        this.analyticsService.accessGlobalMenuDocument('legal notice');
        popover.close();
    }

    onFaqClick(popover: PopoverComponent): void {
        this.analyticsService.accessGlobalMenuDocument('faq');
        popover.close();
    }

    logout(popover: PopoverComponent): void {
        popover.close();
        this.securityService.logout(AUTH_CONFIG_IDS.INSTALLER);
    }
}
